import { createContext, useContext, useEffect, useMemo, useReducer } from 'react';
import { SettingValueUnit } from '../../../api';
import useBiddingSetting from '../../../utilities/useBiddingSetting';
import { VehicleDiagramComment } from '../components/VehicleDiagram';
import { CreateSaleTransactionFormValues } from '.';

export enum Page {
    VehiclePrefill = 'vehiclePrefill',
    VehicleForm = 'vehicleForm',
    OfferSession = 'offerSession',
}
const DEFAULT_HANDLING_CHARGES = { value: 15000, unit: SettingValueUnit.Amount };
const DEFAULT_MARGIN = { value: 0, unit: SettingValueUnit.Amount };

export const defaultValue: CreateSaleTransactionFormValues = {
    vehicle: {
        number: '',
        accessCode: '',
        beExported: true,
        intendedDeregistrationDate: null,
        make: '',
        model: '',
        primaryColour: '',
        manufacturingYear: null,
        engineNumber: '',
        chassisNumber: '',
        maximumPowerOutput: '',
        openMarketValue: null,
        originalRegistrationDate: null,
        firstRegistrationDate: null,
        transferCount: null,
        actualARFPaid: null,
        parfEligibility: true,
        parfEligibilityDate: null,
        coeExpiryDate: null,
        coeCategory: '',
        coePeriodYear: null,
        mileage: null,
        importMethod: '',
        secondaryColour: '',
        opcCashRebateAmount: null,
        opcCashRebateEligibilityExpiryDate: null,
        opcCashRebateEligibility: '',
        pqpPaid: null,
        totalRebateAmount: null,
        ltaMessage: '',
        coeRebateAmount: null,
        parfRebateAmount: null,
        qpPaid: null,
        setOfKeys: null,
        ownerHandbookCollected: false,
    },
    photos: [],
    handover: {
        targetHandoverDateTime: null,
    },
    action: null,
    salesConsultant: null,
    handlingCharges: DEFAULT_HANDLING_CHARGES,
    margin: DEFAULT_MARGIN,
};

export type State = {
    prefillRaw: string;
    formValue: CreateSaleTransactionFormValues;
    currentPage: Page;
    comments: VehicleDiagramComment[];
};

export type SetPrefillRaw = { type: 'setPrefillRaw'; prefillRaw: string };
export type SetFormValue = { type: 'setFormValue'; formValue: CreateSaleTransactionFormValues };
export type SetCurrentPage = { type: 'setCurrentPage'; currentPage: Page };
export type SetComments = { type: 'setComments'; comments: VehicleDiagramComment[] };

export type Action = SetPrefillRaw | SetFormValue | SetCurrentPage | SetComments;

const reducer = (state: State, action: Action): State => {
    switch (action.type) {
        case 'setPrefillRaw':
            return { ...state, prefillRaw: action.prefillRaw };

        case 'setFormValue':
            return { ...state, formValue: action.formValue };

        case 'setCurrentPage':
            return { ...state, currentPage: action.currentPage };

        case 'setComments':
            return { ...state, comments: action.comments };

        default:
            return state;
    }
};

export const useCreateSalesTransactionReducer = () =>
    useReducer(reducer, {
        prefillRaw: '',
        formValue: defaultValue,
        currentPage: Page.VehiclePrefill,
        comments: [],
    });

export type ActionMethods = {
    setPrefillRaw: (prefillRaw: string) => void;
    setFormValue: (formValue: CreateSaleTransactionFormValues) => void;
    setCurrentPage: (currentPage: Page) => void;
    setComments: (comments: VehicleDiagramComment[]) => void;
};

export type CreateSalesTransactionContext = {
    state: State;
    actions: ActionMethods;
};

const Context = createContext<CreateSalesTransactionContext | null>(null);

export const useCreateSalesTransactionState = () => {
    const context = useContext(Context);

    if (!context) {
        throw new Error('useCreateSalesTransactionState must be used within a CreateSalesTransactionStateProvider');
    }

    return context;
};

export type CreateSalesTransactionStateProviderProps = {
    children: JSX.Element | React.ReactNode;
};

const CreateSalesTransactionStateProvider = ({ children }: CreateSalesTransactionStateProviderProps) => {
    const [state, dispatch] = useCreateSalesTransactionReducer();

    const { biddingSetting } = useBiddingSetting('cache-first');
    const { defaultHandlingCharges, defaultMargin } = useMemo(() => {
        if (!biddingSetting?.handlingFees) {
            return { defaultHandlingCharges: DEFAULT_HANDLING_CHARGES, defaultMargin: DEFAULT_MARGIN };
        }

        const matchedHandlingCharges = biddingSetting.handlingFees.isPercentageDefault
            ? { value: biddingSetting.handlingFees.percentage, unit: SettingValueUnit.Percentage }
            : { value: biddingSetting.handlingFees.amount, unit: SettingValueUnit.Amount };

        const matchedMargin = biddingSetting.margin.isPercentageDefault
            ? { value: biddingSetting.margin.percentage, unit: SettingValueUnit.Percentage }
            : { value: biddingSetting.margin.amount, unit: SettingValueUnit.Amount };

        return { defaultHandlingCharges: matchedHandlingCharges, defaultMargin: matchedMargin };
    }, [biddingSetting]);

    useEffect(() => {
        if (!defaultHandlingCharges || typeof defaultHandlingCharges.value !== 'number') {
            return;
        }

        const nextHandlingCharges = {
            value: defaultHandlingCharges.value,
            unit: defaultHandlingCharges.unit ?? SettingValueUnit.Amount,
        };

        // Only apply the server default if the user hasn't changed it yet.
        const current = state.formValue.handlingCharges;
        const isStillDefault =
            current?.value === DEFAULT_HANDLING_CHARGES.value && current?.unit === DEFAULT_HANDLING_CHARGES.unit;

        if (!isStillDefault) {
            return;
        }

        // Avoid dispatch loops when the server default equals the current value.
        if (current?.value === nextHandlingCharges.value && current?.unit === nextHandlingCharges.unit) {
            return;
        }

        dispatch({
            type: 'setFormValue',
            formValue: {
                ...state.formValue,
                handlingCharges: nextHandlingCharges,
            },
        });
    }, [defaultHandlingCharges, dispatch, state.formValue]);

    useEffect(() => {
        if (!defaultMargin || typeof defaultMargin.value !== 'number') {
            return;
        }

        const nextMargin = {
            value: defaultMargin.value,
            unit: defaultMargin.unit ?? SettingValueUnit.Amount,
        };

        // Only apply the server default if the user hasn't changed it yet.
        const current = state.formValue.margin;
        const isStillDefault = current?.value === DEFAULT_MARGIN.value && current?.unit === DEFAULT_MARGIN.unit;

        if (!isStillDefault) {
            return;
        }

        // Avoid dispatch loops when the server default equals the current value.
        if (current?.value === nextMargin.value && current?.unit === nextMargin.unit) {
            return;
        }

        dispatch({
            type: 'setFormValue',
            formValue: {
                ...state.formValue,
                margin: nextMargin,
            },
        });
    }, [defaultMargin, dispatch, state.formValue]);

    const actions = useMemo(
        (): ActionMethods => ({
            setPrefillRaw: prefillRaw => dispatch({ type: 'setPrefillRaw', prefillRaw }),
            setFormValue: formValue => dispatch({ type: 'setFormValue', formValue }),
            setCurrentPage: currentPage => dispatch({ type: 'setCurrentPage', currentPage }),
            setComments: comments => dispatch({ type: 'setComments', comments }),
        }),
        [dispatch]
    );

    const context = useMemo((): CreateSalesTransactionContext => ({ state, actions }), [state, actions]);

    return <Context.Provider value={context}>{children}</Context.Provider>;
};

export default CreateSalesTransactionStateProvider;
